import {
    Button,
    CircularProgress,
    Grid,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Toolbar,
    Typography
} from "@mui/material";
import { FileDownload } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import { Moment } from "moment";
import { useState } from "react";
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import useCores from "../../../core/hooks/useCores";
import { useMoeda } from "../../../core/hooks/useMoeda";
import { useContas } from "../../hooks/useContas";
import { IItemMaisVendido } from "../../interfaces/IItemMaisVendido";

export const RelatorioItensMaisVendidos = () => {
    const { real } = useMoeda();
    const { buscarItensMaisVendidos, exportarItensMaisVendidos, loading } = useContas();

    const [dataInicio, setDataInicio] = useState<Moment | null>(null);
    const [dataFim, setDataFim] = useState<Moment | null>(null);
    const [referenciaTipo, setReferenciaTipo] = useState('');
    const [dados, setDados] = useState<IItemMaisVendido[]>([]);

    const cores = useCores('vibrant', Math.min(dados.length, 5));

    const handleFiltrar = async () => {
        const resultado = await buscarItensMaisVendidos(
            dataInicio ? dataInicio.format('YYYY-MM-DD') : undefined,
            dataFim ? dataFim.format('YYYY-MM-DD') : undefined,
            referenciaTipo || undefined
        );
        if (resultado) setDados(resultado);
    };

    return (
        <div style={{ maxWidth: '95%', margin: '0 auto', paddingTop: '1rem' }}>
            <Paper sx={{ padding: '1rem', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <DatePicker
                        label="Data início"
                        value={dataInicio}
                        onChange={setDataInicio}
                        format="DD/MM/YYYY"
                        slotProps={{ textField: { size: 'small' } }}
                    />
                    <DatePicker
                        label="Data fim"
                        value={dataFim}
                        onChange={setDataFim}
                        format="DD/MM/YYYY"
                        slotProps={{ textField: { size: 'small' } }}
                    />
                    <Select
                        value={referenciaTipo}
                        onChange={(e) => setReferenciaTipo(e.target.value)}
                        displayEmpty
                        size="small"
                        sx={{ width: 200 }}
                    >
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="produto">Produto</MenuItem>
                        <MenuItem value="entrega">Entrega</MenuItem>
                        <MenuItem value="item">Item</MenuItem>
                        <MenuItem value="subitem">Subitem</MenuItem>
                    </Select>
                    <Button variant="contained" size="small" onClick={handleFiltrar} disabled={loading}>
                        {loading ? <CircularProgress size={18} /> : 'Filtrar'}
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={loading ? <CircularProgress size={14} /> : <FileDownload />}
                        onClick={() => exportarItensMaisVendidos(
                            dataInicio ? dataInicio.format('YYYY-MM-DD') : undefined,
                            dataFim ? dataFim.format('YYYY-MM-DD') : undefined,
                            referenciaTipo || undefined
                        )}
                        disabled={loading}
                    >
                        Exportar
                    </Button>
                </div>
            </Paper>

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                    <CircularProgress />
                </div>
            ) : dados.length === 0 ? (
                <Paper sx={{ padding: '2rem' }}>
                    <Typography align="center">
                        Nenhum dado encontrado. Aplique os filtros e clique em Filtrar.
                    </Typography>
                </Paper>
            ) : (
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ padding: '0.5rem', height: '500px' }}>
                            <Typography sx={{ padding: '1rem', position: 'absolute' }} variant="subtitle1" fontWeight="bold">
                                Top 5 itens mais vendidos
                            </Typography>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={dados.slice(0, 5)}
                                        dataKey="quantidade"
                                        nameKey="descricao"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={80}
                                        label={({ name }) => name.length > 10 ? `${name.substring(0, 10)}...` : name}
                                    >
                                        {dados.slice(0, 5).map((entry, index) => (
                                            <Cell key={`cell-${entry.descricao}-${index}`} fill={cores[index]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value: number) => `${value} unidades`} />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper>
                            <TableContainer>
                                <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 } }}>
                                    <Typography variant="h6" fontWeight="800">
                                        Itens mais vendidos
                                    </Typography>
                                </Toolbar>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Descrição</TableCell>
                                            <TableCell>Quantidade</TableCell>
                                            <TableCell>Total</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {dados.map((item, index) => (
                                            <TableRow key={`item-${item.descricao}-${index}`}>
                                                <TableCell>{item.descricao}</TableCell>
                                                <TableCell>{item.quantidade}</TableCell>
                                                <TableCell>{real(item.valorTotal)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>
                </Grid>
            )}
        </div>
    );
};
